// WWEIA food-type → brand-expertise subtype bridge.
//
// The brand picker (src/lib/pickPrimaryBrand.js) classifies a brand
// as primary / secondary / inclusion using a `subtype` axis defined
// in src/data/brandExpertise.js (cookie, candy, sauce, deli, etc.).
// The picker reads subtype from `findIngredient(canonicalId).subtype`
// — but that only works when the canonical is in our bundled registry.
//
// This map closes the gap. When we DON'T have the canonical in the
// registry but DO have a WWEIA typeId (from typeIdForCanonical, from
// inferFoodTypeFromName, or from OFF/USDA-derived data), we can still
// resolve a subtype by looking up the typeId here.
//
// Net effect: a UPC for an unknown canonical that OFF tags as
// "en:cookies" / "en:chocolate-biscuits" maps to typeId=wweia_baked_sweets
// → subtype=cookie. The picker can then demote M&M's-on-the-cookie
// even though the canonical itself was never registered.
//
// Coverage notes:
//   * Mapped to ONE of the brand-expertise subtypes — not all
//     WWEIA types have a corresponding subtype. WWEIA "vegetables"
//     or "fruits" have no brand-expertise mapping (no curated brand
//     in our seed produces those as inclusion-licensors), so they
//     stay unmapped (return null).
//   * Coarse mapping is fine. WWEIA's "wweia_poultry" maps to
//     "chicken_cut" even though it also covers turkey — the picker
//     uses subtype as a coarse demotion gate, not for product
//     identity. Refining further would need MORE subtypes, not a
//     finer typeId map.

export const SUBTYPE_FOR_TYPE_ID = {
  // ── dairy ─────────────────────────────────────────────
  wweia_cheese:           "cheese",
  wweia_yogurt:           "yogurt",
  wweia_milk:             "milk",
  wweia_butter:           "butter",
  wweia_ice_cream:        "ice_cream",

  // ── meat & sausages ───────────────────────────────────
  wweia_poultry:          "chicken_cut",
  wweia_hot_dogs:         "sausage",
  wweia_sausages:         "sausage",

  // ── pantry baked / sweets ─────────────────────────────
  wweia_baked_sweets:     "cookie",      // cookies + pastries roll up here
  wweia_crackers:         "cracker",
  wweia_candy:            "candy",
  wweia_cereal:           "cereal",
  wweia_bread:            "bread",

  // ── pantry shelf-stable / boxed meals ─────────────────
  wweia_pizza:            "boxed_meal",
  wweia_sandwiches:       "boxed_meal",
  wweia_dumplings:        "boxed_meal",
  wweia_frozen_meals:     "boxed_meal",
  wweia_soups:            "boxed_meal",

  // ── pantry baking ─────────────────────────────────────
  wweia_baking_essentials: "baking",
  wweia_flour:             "baking",
  wweia_sugars:            "sweetener",

  // ── pantry sauces / condiments / dressings ────────────
  wweia_ketchup:           "sauce",
  wweia_hot_sauce:         "sauce",
  wweia_bbq_sauce:         "sauce",
  wweia_pesto:             "sauce",
  wweia_salsa:             "sauce",
  wweia_asian_sauces:      "sauce",
  wweia_soy_sauce:         "sauce",
  wweia_fish_sauce:        "sauce",
  wweia_worcestershire:    "sauce",
  wweia_miso:              "sauce",
  wweia_other_sauces:      "sauce",
  wweia_mustard:           "condiment",
  wweia_mayo:              "condiment",
  wweia_salad_dressing:    "dressing",
  wweia_tahini:            "spread",

  // ── pantry beverages ──────────────────────────────────
  wweia_alcohol:           "beverage",
  wweia_juices:            "beverage",
  wweia_coffee_tea:        "beverage",
};

// Tolerant lookup. Accepts the full "wweia_*" id or a bare token
// ("cookies" → "wweia_baked_sweets" → "cookie") so callers can
// pass either form. Returns null when no mapping fits.
export function subtypeForTypeId(typeId) {
  if (!typeId) return null;
  const slug = String(typeId).toLowerCase();
  // Direct hit on the full WWEIA id
  if (SUBTYPE_FOR_TYPE_ID[slug]) return SUBTYPE_FOR_TYPE_ID[slug];
  // Bare token — synthesize the WWEIA prefix and re-check
  const wweia = slug.startsWith("wweia_") ? slug : `wweia_${slug}`;
  return SUBTYPE_FOR_TYPE_ID[wweia] || null;
}

// Hub → subtype fallback. Most canonicals don't carry an explicit
// `subtype` field; instead they inherit from their `parentId` hub
// (cheddar, mozzarella, gruyere all live under cheese_hub). When
// the canonical lacks both an explicit subtype and a WWEIA mapping,
// derive one from the hub. Critical for the brand picker's Tier-1.5
// subtype soft-fallback to fire across the whole dairy + bakery +
// pantry registry without hand-stamping every canonical.
export const HUB_TO_SUBTYPE = {
  // dairy
  cheese_hub:    "cheese",
  milk_hub:      "milk",
  yogurt_hub:    "yogurt",
  butter_hub:    "butter",
  ice_cream_hub: "ice_cream",
  // pantry — staples
  flour_hub:     "baking",
  sugar_hub:     "sweetener",
  bread_hub:     "bread",
  pasta_hub:     "pasta",
  rice_hub:      "rice",
  oil_hub:       "oil",
  vinegar_hub:   "vinegar",
  cereal_hub:    "cereal",
  // meat / poultry / fish
  beef_hub:      "beef_cut",
  pork_hub:      "pork_cut",
  chicken_hub:   "chicken_cut",
  turkey_hub:    "turkey_cut",
  fish_hub:      "fish",
  // produce — added 2026-05-04 after the "Anaheim Pepper showing
  // Pepsi/Dr. Pepper/Coca-Cola/Faygo brands" bug. Hub slugs match
  // the assignments in src/data/ingredients.js (the parentId loop
  // around line 3690). Update both files together if hub naming
  // changes.
  vegetable_hub:        "vegetable_fresh",
  pepper_hub:           "vegetable_fresh",
  root_vegetable_hub:   "vegetable_fresh",
  leafy_green_hub:      "vegetable_fresh",
  legume_hub:           "vegetable_fresh",
  fungi_hub:            "vegetable_fresh",
  fruit_hub:            "fruit_fresh",
  citrus_hub:           "fruit_fresh",
  berry_hub:            "fruit_fresh",
  herb_hub:             "herb_fresh",
};

export function subtypeForHub(hubId) {
  if (!hubId) return null;
  return HUB_TO_SUBTYPE[String(hubId).toLowerCase()] || null;
}
